import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { apiZodios } from '~api/apiZodios'
import {
  setCommunicationAutomations,
  setCurrentProcess,
  setCurrentWorkflow,
  setEmailCapability,
  setExpressions,
  setFlowData,
  setSnackbarConfiguration,
  setStepNotifications,
  setUserId,
} from '~redux/slices/processEditSlice'
import { type AppDispatch } from '~redux/store'
import {
  type APIAutomation,
  type Block,
  type BlockId,
  type CaseRoleAutomation,
  type CaseStatusAutomation,
  type CommunicationAutomation,
  type Connector,
  type DemoBlock,
  type DocumentStep,
  type Expression,
  type ExpressionId,
  type LicenseBlock,
  type ProcessId,
  type RedactionBlock,
  type ReviewBlock,
  type StepNotification,
  type TagAutomation,
} from '~types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Shapes returned by list-block-configurations — serialized from the
// FormStep/BillStep models directly, not the enriched Step-joined types.
export interface BlockFormStep {
  id: string
  step: string // StepId
  block: string // BlockId
  name: string
  description: string
  external: boolean
  due?: string | null
  sla?: string | null
  button_name?: string | null
  requires_submission_confirmation?: boolean
  ai_review_prompt?: string | null
  calculated_tags?: Array<{ tag: string; expression: string | null }>
}

export interface BlockBillStep {
  id: string
  step: string // StepId
  block: string // BlockId
  name: string
  description: string
  installments: number
  account?: string | null
  distribution?: string | null
  allow_ach?: boolean
}

export interface BlockConfigurations {
  form_steps: Record<string, BlockFormStep>
  bill_steps: Record<string, BlockBillStep>
  status_automations: Record<string, CaseStatusAutomation>
  tag_automations: Record<string, TagAutomation>
  role_automations: Record<string, CaseRoleAutomation>
  communication_automations: Record<string, CommunicationAutomation>
  document_steps: Record<string, DocumentStep>
  license_blocks: Record<string, LicenseBlock>
  demo_blocks: Record<string, DemoBlock>
  api_automations: Record<string, APIAutomation>
  redaction_blocks: Record<string, RedactionBlock>
  review_blocks: Record<string, ReviewBlock>
}

export interface ProcessFlowData {
  blocks: Block[]
  connectors: Connector[]
  blockConfigurations: BlockConfigurations
  stepNotifications: Record<BlockId, StepNotification[]>
  expressions: Record<ExpressionId, Expression>
}

export interface UseProcessFlowDataResult {
  loading: boolean
  error: string | null
  reload: () => void
}

export const useProcessFlowData = (
  organizationHandle: string | undefined,
  processCode: string | undefined,
  userId: string | undefined,
): UseProcessFlowDataResult => {
  const dispatch = useDispatch<AppDispatch>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!organizationHandle || !processCode || !userId) return

    let cancelled = false
    setLoading(true)
    setError(null)

    const load = async (): Promise<void> => {
      const { process } = await apiZodios.cases_getProcess({
        process_info_args: {
          process_code: processCode,
          owner_handle: organizationHandle,
        },
      })

      if (cancelled) return

      dispatch(setUserId({ userId }))
      dispatch(setCurrentProcess({ process }))

      const processId = process.id as ProcessId
      const organizationIdentityId = process.owner.id as string

      const [blocksResult, connectorsResult, expressionsResult, workflowResult] = await Promise.all(
        [
          apiZodios.cases_listBlocks({
            user_id: userId,
            organization_identity_id: organizationIdentityId,
            process_version: processId,
          }),
          apiZodios.cases_listConnectors({
            user_id: userId,
            organization_identity_id: organizationIdentityId,
            process_version_id: processId,
          }),
          apiZodios.cases_listExpressions({ process_id: processId }),
          apiZodios.cases_getWorkflow({
            user_id: userId,
            organization_identity_id: organizationIdentityId,
            workflow_code: processCode,
          }),
        ],
      )

      if (cancelled) return

      dispatch(setCurrentWorkflow({ workflow: workflowResult.workflow }))

      const blocks: Block[] = blocksResult.blocks
      const connectors: Connector[] = connectorsResult.connectors

      const expressionsRecord = expressionsResult.data.reduce<Record<ExpressionId, Expression>>(
        (acc: Record<ExpressionId, Expression>, expr: Expression) => {
          acc[expr.id as ExpressionId] = expr
          return acc
        },
        {},
      )
      dispatch(setExpressions({ expressions: expressionsRecord }))

      apiZodios
        .cases_getOrganizationSourceEmails({
          user_id: userId,
          organization_identity_id: organizationIdentityId,
        })
        .then((result) => {
          if (!cancelled) dispatch(setEmailCapability({ emailCapability: result }))
        })
        .catch(() => {
          if (!cancelled)
            dispatch(
              setSnackbarConfiguration({
                show: true,
                message: 'Failed to load email configuration',
                severity: 'error',
              }),
            )
        })

      const blockIds = blocks.map((b) => b.id as BlockId)

      const blockConfigurations: BlockConfigurations =
        await apiZodios.cases_listBlockConfigurations({
          user_id: userId,
          organization_identity_id: organizationIdentityId,
          block_ids: blockIds,
        })

      if (cancelled) return

      const { step_assignments: stepAssignments } = await apiZodios.cases_listStepAssignments({
        user_id: userId,
        organization_identity_id: organizationIdentityId,
        block_ids: blockIds,
      })

      if (cancelled) return

      const stepNotificationsRecord = stepAssignments.reduce<Record<BlockId, StepNotification[]>>(
        (acc, n): Record<BlockId, StepNotification[]> => {
          acc[n.block as BlockId] = (acc[n.block as BlockId] || []).concat(n)
          return acc
        },
        {},
      )
      dispatch(setStepNotifications({ stepNotifications: stepNotificationsRecord }))
      dispatch(setFlowData({ blocks, connectors, blockConfigurations }))
      dispatch(
        setCommunicationAutomations({
          communicationAutomations: blockConfigurations.communication_automations ?? {},
        }),
      )

      setLoading(false)
    }

    load().catch((err) => {
      if (!cancelled) {
        console.error('ProcessEditFlowPage data load failed:', err)
        setError('Failed to load process data')
        setLoading(false)
      }
    })

    return (): void => {
      cancelled = true
    }
  }, [organizationHandle, processCode, userId, reloadKey, dispatch])

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])

  return { loading, error, reload }
}
